// Controlling a device over BLE when there is no Wi-Fi.
//
// Lives apart from bleClient because it now needs an AUTHENTICATED session, and
// bleSession imports the transport — putting this back in bleClient would make
// the two import each other.
//
// A CONNECTION IS HELD OPEN across taps. Every command otherwise pays for a
// scan, a connect, an MTU negotiation and a three-message handshake — several
// seconds in front of every button press. The session is cached per unique
// number and reused until it drops.
//
// The device advertises `Smartify<UniqueNumber>` while the app knows it by its
// unique number, so finding one means scanning and matching the name suffix.

import { BleClient, ScanMode } from '@capacitor-community/bluetooth-le';
import {
  ensureBleReady, bleAutoSupported, stopScan, isBleOffError, BLE_OFF,
} from './bleClient';
import { BLE_NAME_PREFIX, SERVICE_UUID, SCAN_TIMEOUT_MS } from './bleConfig';
import { openSession } from './bleSession';
import { emitLocalMessage } from '../mqtt/mqttClient';

let link = null; // { uniqueNumber, session }

export const connectedUniqueNumber = () => (link ? link.uniqueNumber : null);

// ── What went OUT over the link ───────────────────────────────────────────
// ⚠️ THE BENCH LOG MUST SEE EVERY WRITE, NOT ONLY ITS OWN BUTTONS (2026-10-25).
// BLE Test logs the probes it sends itself, but once the type's REAL controls
// are drawn there (a curtain's Open/Stop/Close goes through publishCommand ->
// sendControlOverBle), those writes bypassed that log — and a bench screen whose
// log is missing half the traffic is worse than no log, because the gap looks
// like a device that did not answer. Anything sending over the control link
// announces itself here instead.
const traceListeners = new Set();
export function onBleTrace(fn) {
  traceListeners.add(fn);
  return () => traceListeners.delete(fn);
}
const trace = (uniqueNumber, message, ok) => {
  traceListeners.forEach((fn) => {
    try { fn(String(uniqueNumber), String(message), ok); } catch (e) { /* ignore */ }
  });
};

// Status notifications from whichever device is currently linked, so a physical
// press still reaches the UI while offline.
const statusListeners = new Set();
export function onBleStatus(fn) {
  statusListeners.add(fn);
  return () => statusListeners.delete(fn);
}

/**
 * A notification from a device over BLE → every listener, AND the app's normal
 * message stream as `<uno>/client` (mqttClient.emitLocalMessage).
 *
 * ⚠️ WITHOUT THE SECOND HALF A DEVICE SCREEN OVER BLE IS DEAF (2026-10-18).
 * Commands already went over BLE (deviceController → sendControlOverBle) but the
 * screens listen only to MQTT, so a button sent and never turned green. The
 * firmware notifies the same `[uno]…#BLE` text it publishes on MQTT, so feeding
 * it into the stream makes every regular screen work over BLE unchanged.
 */
export function relayBleStatus(uniqueNumber, text) {
  const uno = String(uniqueNumber);
  statusListeners.forEach((fn) => {
    try { fn(uno, text); } catch (e) { /* ignore listener error */ }
  });
  emitLocalMessage(`${uno}/client`, text);
}

/**
 * Take over a session someone else opened (the BLE Test screen's "Open full
 * screen"), so the device screen uses the live link instead of scanning and
 * handshaking again (up to 15 s). The opener must route its notifications to
 * relayBleStatus and must not close the session when it unmounts.
 */
export async function adoptControlLink(uniqueNumber, session) {
  const uno = String(uniqueNumber);
  if (link && link.session !== session) {
    try { await link.session.close(); } catch (e) { /* already gone */ }
  }
  link = { uniqueNumber: uno, session };
}

/** True while this device has a live BLE control link. */
export const hasControlLink = (uniqueNumber) => !!(link && link.uniqueNumber === String(uniqueNumber)
  && !link.session.isClosed());

async function findByUniqueNumber(uniqueNumber) {
  const target = String(uniqueNumber).toUpperCase();
  let found = null;
  // Offline control is the path where a switched-off radio is LEAST visible:
  // there is no scan list to look empty, only a button that does nothing. The
  // typed error propagates so the caller can say which of the two it was.
  await ensureBleReady();
  await BleClient.requestLEScan({
    services: [SERVICE_UUID],
    allowDuplicates: false,
    // Active scan — the name is in the scan response, and matching by unique
    // number needs the name.
    scanMode: ScanMode.SCAN_MODE_LOW_LATENCY,
  }, (result) => {
    const name = (result.localName || result.device?.name || '').toUpperCase();
    if (!name.startsWith(BLE_NAME_PREFIX.toUpperCase())) return;
    if (name.includes(target) && !found) found = result.device.deviceId;
  });
  // Poll rather than always waiting the full window: a device that answers in
  // 200 ms should not cost the user 15 seconds.
  const deadline = Date.now() + SCAN_TIMEOUT_MS;
  while (!found && Date.now() < deadline) {
    // eslint-disable-next-line no-await-in-loop
    await new Promise((r) => { setTimeout(r, 250); });
  }
  await stopScan();
  return found;
}

/**
 * An authenticated session for this device, reusing the open one if there is
 * one. Returns null when the device cannot be found or authenticated.
 */
export async function ensureControlLink(uniqueNumber, ownerSecret = null) {
  // ⚠️ NATIVE ONLY, even though the transport now works on the web too.
  //
  // Everything here finds a device BY ITSELF: findByUniqueNumber scans and
  // matches a name, with nobody watching. The web has no such scan — the user
  // picks the device in a browser chooser, per connection — so on the web this
  // would put a permission dialog in front of every offline button press. A
  // control path that needs a click is not a fallback.
  if (!bleAutoSupported()) return null;
  const uno = String(uniqueNumber);

  if (link && link.uniqueNumber === uno && !link.session.isClosed()) return link.session;
  if (link) {
    await link.session.close();
    link = null;
  }

  const deviceId = await findByUniqueNumber(uno);
  if (!deviceId) return null;

  const opened = await openSession(deviceId, uno, {
    ownerSecret,
    onStatus: (text) => relayBleStatus(uno, text),
    onDisconnect: () => { link = null; },
  });
  if (!opened.ok) return null;

  link = { uniqueNumber: uno, session: opened.session };
  return opened.session;
}

/**
 * Send a control command over BLE.
 *
 * -> `{ ok, code }`. `code` is `BLE_OFF` when the radio is switched off, and
 * null for an ordinary "did not find it / did not authenticate".
 *
 * ⚠️ THE DISTINCTION IS THE WHOLE POINT. Both used to come back as plain
 * `false`, so a phone with Bluetooth turned off told the user "move closer and
 * try again" — advice that cannot possibly work, for a fault they could fix in
 * two seconds if anything had said what it was.
 */
export async function sendControlOverBle(uniqueNumber, message, ownerSecret = null) {
  try {
    const session = await ensureControlLink(uniqueNumber, ownerSecret);
    if (!session) { trace(uniqueNumber, message, false); return { ok: false, code: null }; }
    await session.send(message);
    trace(uniqueNumber, message, true);
    return { ok: true, code: null };
  } catch (e) {
    trace(uniqueNumber, message, false);
    return { ok: false, code: isBleOffError(e) ? BLE_OFF : null };
  }
}

/** Drop the held connection — call when leaving a device screen. */
export async function closeControlLink() {
  if (!link) return;
  await link.session.close();
  link = null;
}
